import { useCallback, useEffect, useMemo, useState } from 'react';

import AuthForm from './components/AuthForm.jsx';
import HistoryPanel from './components/HistoryPanel.jsx';
import Navbar from './components/Navbar.jsx';
import SearchBar from './components/SearchBar.jsx';
import StockDashboard from './components/StockDashboard.jsx';
import { useAuth } from './hooks/useAuth.jsx';
import { useHistory } from './hooks/useHistory.js';
import { useStock, useTickerIndex } from './hooks/useStock.js';

/**
 * Application shell: search, dashboard, personal history and auth.
 *
 * Data flow is strictly one-directional — the shell owns the selected ticker,
 * hooks own the network state, components stay presentational.
 */
export default function App() {
  const { token, user } = useAuth();
  const index = useTickerIndex();
  const [ticker, setTicker] = useState(null);
  const [showAuth, setShowAuth] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const stock = useStock(ticker);
  const history = useHistory(token);

  // First paint: open the highest-scoring constituent so the terminal is never empty.
  useEffect(() => {
    if (ticker || index.status !== 'ready' || index.data.length === 0) return;
    const best = [...index.data].sort((left, right) => (right.score ?? 0) - (left.score ?? 0))[0];
    setTicker(best.ticker);
  }, [ticker, index.status, index.data]);

  const savedTickers = useMemo(
    () => new Set(history.items.map((item) => item.ticker)),
    [history.items],
  );
  const isSaved = Boolean(ticker && savedTickers.has(ticker));

  const handleSave = useCallback(async () => {
    if (!ticker) return;
    if (!user) {
      setShowAuth(true);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await history.save(ticker);
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  }, [ticker, user, history]);

  const asOf = stock.data?.date ?? index.data[0]?.date ?? null;

  return (
    <div className="min-h-screen bg-terminal-950">
      <Navbar asOf={asOf} onRequestSignIn={() => setShowAuth(true)} />

      <main className="mx-auto grid max-w-7xl gap-4 px-4 py-5 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="flex flex-col gap-4">
          <SearchBar
            options={index.data}
            loading={index.status === 'loading'}
            onSelect={setTicker}
          />

          {index.status === 'error' ? (
            <p className="panel border-bear/40 px-4 py-3 text-sm text-bear">{index.error}</p>
          ) : null}

          {saveError ? (
            <p className="panel border-bear/40 px-4 py-3 text-sm text-bear">{saveError}</p>
          ) : null}

          <StockDashboard
            stock={stock.data}
            status={stock.status}
            error={stock.error}
            onSave={handleSave}
            saving={saving}
            saved={isSaved}
            signedIn={Boolean(user)}
          />
        </div>

        <aside className="flex flex-col gap-4">
          <HistoryPanel
            history={history}
            signedIn={Boolean(user)}
            selectedTicker={ticker}
            onSelect={setTicker}
            onRequestSignIn={() => setShowAuth(true)}
          />

          <section className="panel px-4 py-4 text-xs leading-relaxed text-slate-500">
            <h2 className="panel-title mb-2">How it works</h2>
            <ol className="list-decimal space-y-1.5 pl-4">
              <li>GitHub Actions pulls two years of daily OHLCV after the 16:30 IST close.</li>
              <li>pandas-ta derives EMA 20/50/200, RSI, MACD and ATR.</li>
              <li>A weighted engine publishes a 0-100 Bull Score and 1D-1Y targets.</li>
              <li>Cloudflare Pages serves the precomputed rows from Neon at the edge.</li>
            </ol>
          </section>
        </aside>
      </main>

      <footer className="mx-auto max-w-7xl px-4 pb-8 pt-2 text-[11px] leading-relaxed text-slate-600">
        NiftySense is an educational analytics tool. It is not investment advice, not a
        recommendation to buy or sell, and not a SEBI-registered research service. Targets are
        model output, not guarantees. Markets carry risk.
      </footer>

      {showAuth ? <AuthModal onClose={() => setShowAuth(false)} /> : null}
    </div>
  );
}

/** Focused modal wrapper around :func:`AuthForm`. */
function AuthModal({ onClose }) {
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in or create an account"
      onClick={onClose}
    >
      <div className="w-full max-w-sm" onClick={(event) => event.stopPropagation()}>
        <AuthForm onAuthenticated={onClose} />
        <button
          type="button"
          className="mt-3 w-full text-center text-xs text-slate-400 underline decoration-dotted hover:text-slate-200"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}
